import React from 'react'
import { X, Calendar, Clock, MapPin, Users } from 'lucide-react'
import { useLocale } from '../contexts/LocaleContext'
import { Shift } from '../types'
import { formatTimeRangeToLocal } from '../utils/timezone'

interface ClaimConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  shift: Shift | null
  isLoading?: boolean
}

const ClaimConfirmModal: React.FC<ClaimConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  shift,
  isLoading = false
}) => {
  const { t } = useLocale()
  
  if (!isOpen || !shift) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <Calendar className="h-6 w-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {t('shifts.claimShift')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-4">
              {t('shifts.claimConfirmMessage')}
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="font-medium text-blue-900 mb-3">{shift.title}</h4>
              
              <div className="space-y-2 text-sm text-blue-700">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  {formatTimeRangeToLocal(shift.start_time, shift.end_time)}
                </div>
                
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  {shift.location}
                </div>
                
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  {shift.current_volunteers}/{shift.max_volunteers} {t('shifts.volunteers')}
                </div>
              </div>
              
              {shift.description && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-sm text-blue-600">{shift.description}</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
            <p className="text-sm text-green-700">
              <strong>{t('shifts.claimConfirmNote')}</strong> {t('shifts.claimConfirmNoteText')}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            disabled={isLoading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm()
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? t('shifts.claiming') : t('shifts.confirmClaim')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ClaimConfirmModal
